import React, { forwardRef, useRef, useEffect, useState } from 'react';
import {
  autoUpdate,
  flip,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
  FloatingPortal,
  FloatingFocusManager,
  FloatingArrow,
  size,
  useMergeRefs,
  arrow,
  Placement,
} from '@floating-ui/react';
import { useSelector } from 'react-redux';
import { RIGHT_MENU_WIDTH_PX } from '../../../components/RightMenu/RightMenu';

const COMMON_STYLES = (predicate: boolean) => ({
  padding: {
    top: 16,
    bottom: 16,
    left: 16,
    right: predicate ? RIGHT_MENU_WIDTH_PX + 16 : 16,
  },
});

interface FloatingMenuRenderProps {
  needsScroll: boolean;
  onClose: () => void;
}

interface FloatingMenuProps {
  children:
    | React.ReactNode
    | ((props: FloatingMenuRenderProps) => React.ReactNode);
  trigger: React.ReactElement;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  placement?: Placement;
  offset?: { mainAxis?: number; crossAxis?: number };
  arrowConfig?: {
    fill?: string;
    stroke?: string;
    strokeWidth?: number;
    width?: number;
    height?: number;
  };
  className?: string;
  innerClassName?: string;
  containerSelector?: string;
  role?: string;
  'aria-label'?: string;
  disabled?: boolean;
  respectRightMenuSpacing?: boolean;
}

const FloatingMenu = forwardRef<HTMLDivElement, FloatingMenuProps>(
  (
    {
      children,
      trigger,
      isOpen,
      onOpenChange,
      placement = 'bottom-start',
      offset: offsetConfig = { mainAxis: 8, crossAxis: -40 },
      arrowConfig = {
        fill: '#ffffff',
        stroke: '#e0e0e0',
        strokeWidth: 1,
        width: 20,
        height: 10,
      },
      className = 'floating-menu',
      innerClassName = 'floating-menu-inner',
      containerSelector = `.${innerClassName}`,
      role = 'menu',
      'aria-label': ariaLabel,
      disabled = false,
      respectRightMenuSpacing = true,
    },
    ref
  ) => {
    const floatingRef = useRef<HTMLDivElement>(null);
    const arrowRef = useRef<SVGSVGElement>(null);
    const [needsScroll, setNeedsScroll] = useState(false);
    const resizeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const isRightMenuVisible = useSelector(
      (reduxState: { layout: { rightMenu: { isVisible: boolean } } }) =>
        reduxState.layout.rightMenu?.isVisible
    );

    const collisionPadding = respectRightMenuSpacing
      ? COMMON_STYLES(isRightMenuVisible).padding
      : COMMON_STYLES(false).padding;

    const { refs, floatingStyles, context } = useFloating({
      open: isOpen,
      onOpenChange,
      middleware: [
        offset(offsetConfig),
        flip({
          crossAxis: false,
          padding: collisionPadding,
        }),
        shift({
          padding: collisionPadding,
        }),
        size({
          apply({ availableHeight, elements }) {
            const maxHeight = Math.max(300, availableHeight - 16);

            // Use requestAnimationFrame to avoid ResizeObserver loops
            requestAnimationFrame(() => {
              if (elements.floating) {
                const menuContainer = elements.floating.querySelector(
                  containerSelector
                ) as HTMLElement;

                if (menuContainer) {
                  const naturalHeight = menuContainer.scrollHeight;
                  const willNeedScroll = naturalHeight > maxHeight;

                  // Use requestAnimationFrame to allow CSS transitions to work
                  requestAnimationFrame(() => {
                    Object.assign(menuContainer.style, {
                      maxHeight: `${maxHeight}px`,
                      overflowY: willNeedScroll ? 'auto' : 'hidden',
                    });
                  });

                  // Debounce the scroll state update to prevent rapid re-renders
                  if (resizeTimeoutRef.current) {
                    clearTimeout(resizeTimeoutRef.current);
                  }

                  resizeTimeoutRef.current = setTimeout(() => {
                    setNeedsScroll(willNeedScroll);
                  }, 16); // ~1 frame delay
                }
              }
            });
          },
          padding: 16,
        }),
        arrow({ element: arrowRef }),
      ],
      placement,
      strategy: 'fixed',
      whileElementsMounted: autoUpdate,
    });

    const click = useClick(context);
    const dismiss = useDismiss(context);
    const roleInteraction = useRole(context, { role: role as any });

    const { getReferenceProps, getFloatingProps } = useInteractions([
      click,
      dismiss,
      roleInteraction,
    ]);

    useEffect(() => {
      return () => {
        if (resizeTimeoutRef.current) {
          clearTimeout(resizeTimeoutRef.current);
        }
      };
    }, []);

    const mergedFloatingRef = useMergeRefs([refs.setFloating, floatingRef]);

    // Dynamic arrow fill based on placement
    const dynamicArrowFill = (() => {
      const side = context.placement.split('-')[0];
      if (arrowConfig.fill === 'auto') {
        return side === 'bottom' ? '#f3f3f3' : '#ffffff';
      }
      return arrowConfig.fill;
    })();

    // Dynamic border overrides based on placement
    const borderOverrides: React.CSSProperties = (() => {
      const side = context.placement.split('-')[0];
      if (side === 'top') {
        return { borderBottom: 'none' };
      } else {
        return { borderTop: 'none' };
      }
    })();

    const renderProps: FloatingMenuRenderProps = {
      needsScroll,
      onClose: () => onOpenChange(false),
    };

    return (
      <div ref={ref}>
        {React.cloneElement(trigger, {
          ref: refs.setReference,
          ...getReferenceProps(),
          'aria-label': ariaLabel,
          disabled,
        })}

        <FloatingPortal>
          {isOpen ? (
            <FloatingFocusManager
              context={context}
              initialFocus={refs.floating}
            >
              <div
                ref={mergedFloatingRef}
                style={{
                  ...floatingStyles,
                  overflow: 'visible',
                }}
                {...getFloatingProps()}
                className={className}
              >
                <FloatingArrow
                  ref={arrowRef}
                  context={context}
                  fill={dynamicArrowFill}
                  stroke={arrowConfig.stroke}
                  strokeWidth={arrowConfig.strokeWidth}
                  width={arrowConfig.width}
                  height={arrowConfig.height}
                />
                <div className={innerClassName} style={borderOverrides}>
                  {typeof children === 'function'
                    ? children(renderProps)
                    : children}
                </div>
              </div>
            </FloatingFocusManager>
          ) : null}
        </FloatingPortal>
      </div>
    );
  }
);

FloatingMenu.displayName = 'FloatingMenu';

export default FloatingMenu;
